import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {EMPTY, forkJoin, Observable, Subject} from 'rxjs';
import {Forecast, FORECAST_METADATA, QuantityType} from 'smag-commons/dto/msg';
import {ActivatedRoute, Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {I18NService} from '../../i18n/i18n.service';
import {MsgBoxService} from '../../shared/msg-box.service';
import {SmagApiService} from '../../api/smag-api.service';
import {catchError, map, shareReplay, switchMap, takeUntil, tap} from 'rxjs/operators';
import {ResizeService} from '../../shared/resize.service';
import * as _ from 'lodash';
import {EnergyCommunityNetwork, mkQuantityCoefficient, mkQuantityUom} from '../../shared/entities';
import {APP_CONFIG_TOKEN, AppConfig} from '../../app.config';
import {QuantityNamePipe} from '../../shared/shared.pipe';
import {HierarchyService} from '../../shared/hierarchy.service';
import {Entity, EntityType} from 'smag-commons/cfg/hierarchy';
import {isDefined} from '../../commons/commons';
import {hasStatisticalProfile, isConsumer, isProducer, isProsumer} from 'smag-commons/fcn';
import {hasFieldEntityDevice} from '../../shared/common';
import {NetworkService} from '../../shared/network.service';
import {TIME_DELTA_QUARTER_HOUR} from '../../shared/line-chart/line-chart.pipe';
import {
    getPodAbsorbedPowerStatisticalProfileDeviceKey,
    getPodInjectedPowerStatisticalProfileDeviceKey,
    getPlantProducedPowerStatisticalProfileDeviceKey
} from 'smag-commons/fcn/statistical-profiles';

enum DataSource {
    MONITORED,
    STATISTICAL
}

// quantities shown depend on entity's type
interface ForecastQuantity {
    quantityName: string;
    quantityType: QuantityType;
    model?: string;
    data: [number, number][]; // forecast data
    uom: string;
    color?: string;
    icon?: string;
}

@Component({
    selector: 'app-forecast',
    templateUrl: './forecast.component.html',
    styleUrls: ['./forecast.component.scss']
})
export class ForecastComponent implements OnInit, OnDestroy {

    private destroyed = new Subject<void>();
    private resourcesUrl: string[];
    private qnp: QuantityNamePipe;

    contentResize: Observable<void>;
    forecast: Observable<Forecast>;
    forecastQuantities: Observable<ForecastQuantity[]>;
    dataSources: { [qt in QuantityType]?: DataSource };

    isDefined = isDefined;
    DataSource = DataSource;
    TIME_DELTA_QUARTER_HOUR = TIME_DELTA_QUARTER_HOUR;

    constructor(@Inject(APP_CONFIG_TOKEN) private readonly cfg: AppConfig,
                private readonly router: Router,
                private readonly route: ActivatedRoute,
                private readonly title: Title,
                private readonly i18n: I18NService,
                private readonly msgBoxService: MsgBoxService,
                private readonly apiService: SmagApiService,
                private readonly hierarchyService: HierarchyService,
                private readonly resizeService: ResizeService,
                private readonly networkService: NetworkService) {
        this.resourcesUrl = [];
        this.qnp = new QuantityNamePipe();
        this.forecast = null;
        this.forecastQuantities = null;
        this.dataSources = {};
        this.contentResize = this.resizeService.observe(['content']).pipe(
            takeUntil(this.destroyed)
        );
    }

    ngOnInit() {
        this.forecast = this.route.paramMap.pipe(
            map(params => params.get('forecastId')),
            tap(forecastId => {
                this.title.setTitle(this.i18n.translate('Forecast'));
            }),
            switchMap(forecastId => this.loadForecast(forecastId).pipe(
                catchError(err => {
                    this.msgBoxService.error(this.i18n.translate('An error occurred while loading forecast {0}', forecastId), err);
                    return EMPTY;
                }))),
            shareReplay(1),
            takeUntil(this.destroyed)
        );
        this.forecastQuantities = this.forecast.pipe(
            map(forecast => this.mkForecastQuantities(forecast)),
            takeUntil(this.destroyed)
        );
        // if the forecast's resource belongs to an energy community, extract data source
        this.forecast.pipe(
            switchMap(forecast => forkJoin([
                this.apiService.getEntity(forecast.entity_id),
                this.networkService.buildECNetworkOfResource(forecast.entity_id)
            ]).pipe(
                map(([resource, network]) => [resource, network, forecast] as [Entity, EnergyCommunityNetwork, Forecast])
            )),
            takeUntil(this.destroyed)
        ).subscribe(([resource, network, forecast]) => {
            this.dataSources = extractDataSources(network, resource, forecast);
            this.resourcesUrl = ['/c', 'ec', 'resources'];
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get gridColor(): string {
        return this.cfg.shared.gridColor;
    }

    get tooltipBorderColor(): string {
        return this.cfg.shared.tooltipBorderColor;
    }

    get tooltipBackgroundColor(): string {
        return this.cfg.shared.tooltipBackgroundColor;
    }

    get selectionLineColor(): string {
        return this.cfg.shared.selectionLineColor;
    }

    onClickGotoResources(entityId: string) {
        this.router.navigate(['/c', 'ec', 'resources'], {
            queryParams: {
                id: entityId
            },
            queryParamsHandling: 'merge'
        }).then();
    }

    private mkForecastQuantities(forecast: Forecast): ForecastQuantity[] {
        return _.keys(forecast.data || {}).map((qt: QuantityType) => {
            const coeff = mkQuantityCoefficient(qt);
            return {
                quantityName: this.i18n.translate(this.qnp.transform(qt)),
                quantityType: qt,
                model: extractModel(forecast, qt),
                data: forecast.data[qt].map(tv => [tv.timestamp, tv.value * coeff]),
                uom: mkQuantityUom(qt),
                color: this.cfg.shared.quantityColors[qt] || this.cfg.shared.defaultColor,
                icon: mkCommunityIcon(qt)
            };
        });
    }

    private loadForecast(forecastId: string): Observable<Forecast> {
        return this.apiService.retrieveForecast(forecastId, true);
    }
}

function extractModel(forecast: Forecast, qt: QuantityType): string {
    if (!forecast.metadata || !forecast.metadata[FORECAST_METADATA.MODELS_KEY]) {
        return null;
    }
    return forecast.metadata[FORECAST_METADATA.MODELS_KEY][qt];
}

function extractDataSources(network: EnergyCommunityNetwork, e: Entity, fc: Forecast): { [qt in QuantityType]?: DataSource } {
    const dataSources: { [qt in QuantityType]?: DataSource } = {};
    Object.keys(fc.data).forEach((qt: QuantityType) => {
        const key = quantityTypeToKey(network, fc.entity_id, e, qt);
        if (hasFieldEntityDevice(e, key)) {
            dataSources[qt] = DataSource.MONITORED;
        } else if (hasStatisticalProfile(e, key)) {
            dataSources[qt] = DataSource.STATISTICAL;
        } else {
            dataSources[qt] = null;
        }
    });
    return dataSources;
}

function quantityTypeToKey(network: EnergyCommunityNetwork, resourceId: string, e: Entity, qt: QuantityType) {
    if (e.type === EntityType.PLANT && qt === QuantityType.ACTIVE_POWER) {
        return getPlantProducedPowerStatisticalProfileDeviceKey();
    } else if (e.type === EntityType.POD) {
        const pod = (network as EnergyCommunityNetwork).pods[resourceId];
        if (pod) {
            if (isConsumer(pod) && qt === QuantityType.ACTIVE_POWER) {
                return getPodAbsorbedPowerStatisticalProfileDeviceKey();
            } else if (isProducer(pod) && qt === QuantityType.ACTIVE_POWER) {
                return getPodInjectedPowerStatisticalProfileDeviceKey();
            } else if (isProsumer(pod)) {
                if (qt === QuantityType.ABSORBED_POWER) {
                    return getPodAbsorbedPowerStatisticalProfileDeviceKey();
                } else if (qt === QuantityType.INJECTED_POWER) {
                    return getPodInjectedPowerStatisticalProfileDeviceKey();
                }
            }
        }
    }
    return null;
}

function mkCommunityIcon(type: QuantityType): string {
    switch (type) {
        case QuantityType.ABSORBED_POWER:
            return 'fal-paper-plane';
        case QuantityType.INJECTED_POWER:
            return 'fal-paper-plane';
        case QuantityType.GENERATED_POWER:
            return 'fal-solar-panel';
        default:
            return 'fal-paper-plane';
    }
}
